import { LineChart } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import { InsightsMetricsSplitValues } from "@octopusdeploy/portal-routes";
import { useEffect } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { InsightsReportLayout } from "~/areas/insights/components/InsightsReportLayout/InsightsReportLayout";
import { ReportMetricsLayout } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import type { ReportMetricsPageProps } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import { useReportFilters } from "~/areas/insights/components/Reports/ReportMetricsLayout/useReportFilters";
import { getChartAxisDateFormatter } from "../../dataTransformation/dateHelpers";
import { getChartReadyInsightsData } from "../../dataTransformation/getChartReadyInsightsData";
import { getEvenlySpacedTickArray, getYAxisDataMax } from "../../dataTransformation/getYAxisDataMax";
import { insightsCadenceLookup, insightsCadenceXAxisTickInterval } from "../../insightsCadence";
import { InsightsTooltip } from "../ChartTooltips/InsightsTooltip/InsightsTooltip";
import { DownloadReportCsvButton } from "../DownloadReportCsvButton";
import { InsightsChartCard } from "../InsightsChartCard";
import { DeploymentFrequencyTrendTables } from "./TrendTables/DeploymentFrequencyTrendTables";
type Props = ReportMetricsPageProps;
function DeploymentFrequencyInsightsInner({ report, bffResponse, split, cadence, busy }: Props) {
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const chartData = getChartReadyInsightsData(bffResponse.Series);
    const currentGranularity = insightsCadenceLookup[cadence].granularity;
    const maxYAxisValue = getYAxisDataMax(chartData, "NumberOfSuccessfulDeployments");
    const dateFormatter = getChartAxisDateFormatter(currentGranularity);
    const showSeries = split !== InsightsMetricsSplitValues.None;
    useEffect(() => {
        dispatchAction("View Deployment Frequency Insights Report ");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (<main>
            <section style={{ padding: `0 ${space[16]} ${space[32]}` }}>
                <InsightsChartCard height={500}>
                    <LineChart data={chartData} loading={Boolean(busy)} dataKey="NumberOfSuccessfulDeployments" yAxisLabel="No. of deployments" xAxisDataKey="StartOfInterval" xAxisType="category" yAxisType="number" yAxisDomain={[0, maxYAxisValue]} yAxisTickArray={getEvenlySpacedTickArray(maxYAxisValue)} xTickFormatter={dateFormatter} tooltip={<InsightsTooltip showSeries={showSeries} dataKey="NumberOfSuccessfulDeployments" valueFormatter={(v) => `${v.toLocaleString()} deployment${v === 1 ? "" : "s"}`} cadence={cadence}/>} xAxisTickInterval={insightsCadenceXAxisTickInterval[cadence]} showLegend={showSeries}/>
                </InsightsChartCard>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: space[8] }}>
                    <DownloadReportCsvButton report={report}/>
                </div>
            </section>
            <section>
                <DeploymentFrequencyTrendTables trends={bffResponse.DeploymentFrequency} spaceId={report.SpaceId}/>
            </section>
        </main>);
}
export function DeploymentFrequencyInsightsPage({ report }: {
    report: InsightsReportResource;
}) {
    const [reportFilter, setReportFilter] = useReportFilters();
    return (<InsightsReportLayout report={report} reportFilter={reportFilter}>
            <ReportMetricsLayout title="Deployment frequency" report={report} trendKey="DeploymentFrequency" reportFilter={reportFilter} setReportFilter={setReportFilter}>
                {(props) => <DeploymentFrequencyInsightsInner {...props}/>}
            </ReportMetricsLayout>
        </InsightsReportLayout>);
}
